import { useEffect, useState } from 'react';
import { Stack, Collapse, Button, Typography } from '@mui/material';
import { Address, CustomerAddress, CustomerInfo, UpdateAddressesRequest } from 'orderme-api-integration-client';
import { useTranslation } from 'react-i18next';
import { CancelButton } from '../../styles/buttonStyles';
import { ChooseAddress } from './chooseAddress';
import { CustomerClient } from '../../helpers/client';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { assignCustomerDetails } from '../../helpers/customerHelper';
import { ButtonLoadingIndicator } from '../../controls';

export interface NewAddressProps {
  onAdded: (addresses: CustomerAddress) => void;
  onCancel?: () => void;
}

export const NewAddress = ({ onAdded, onCancel }: NewAddressProps) => {
  const customerClient: CustomerClient = new CustomerClient();
  const { t } = useTranslation();
  const userInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const [existingAddresses, setExistingAddresses] = useState<CustomerAddress[]>([]);
  const [newAddress, setNewAddress] = useState<CustomerAddress>();
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [isUniqueAddress, setIsUniqueAddress] = useState(true);

  useEffect(() => {
    if (Object.keys(userInfo).length === 0) {
      assignCustomerDetails();
      setExistingAddresses([]);
    } else {
      let addresses = userInfo?.addresses.map((item) => item.address) || [];
      setExistingAddresses(
        addresses.sort((a, b) => {
          if (Number(b.isPrimary) !== Number(a.isPrimary)) {
            return Number(b.isPrimary) - Number(a.isPrimary);
          }
          return a.address.localeCompare(b.address, 'lt');
        })
      );
    }
  }, [userInfo]);

  useEffect(() => {
    if (!newAddress) {
      setIsUniqueAddress(true);
      return;
    }
    if (existingAddresses?.length > 0 && existingAddresses.some((a) => areAddressEqual(a, newAddress))) {
      setIsUniqueAddress(false);
    } else {
      setIsUniqueAddress(true);
    }
  }, [newAddress, existingAddresses]);

  const addNewAddress = () => {
    validateAddress();
    const customerAddresses = [...existingAddresses, newAddress];
    updateAddresses(customerAddresses);
  };

  const validateAddress = () => {
    if (!newAddress) {
      return;
    }

    if (existingAddresses?.length > 0 && existingAddresses.some((a) => areAddressEqual(a, newAddress))) {
      setIsUniqueAddress(false);
      return;
    }
  };

  const areAddressEqual = (address1: CustomerAddress, address2: CustomerAddress) => {
    if (!address1 || !address2) {
      return false;
    }

    if (address1.address !== address2.address) {
      return false;
    }

    return address1.apartmentNumber === address2.apartmentNumber;
  };

  const selectNewAddress = (address: Address, apartmentNo: string) => {
    if (address) {
      setNewAddress({
        address: address.fullAddress,
        apartmentNumber: apartmentNo,
      } as CustomerAddress);
    } else {
      setNewAddress(null);
    }
  };

  const updateAddresses = async (customerAddresses: CustomerAddress[]) => {
    setIsAddingAddress(true);
    var updateRequest = {
      addresses: customerAddresses,
    } as UpdateAddressesRequest;

    try {
      await customerClient.changeAddresses(userInfo.id, updateRequest);
      await assignCustomerDetails();
      setNewAddress(null);
      onAdded(newAddress);
    } catch (exception) {
      console.error(exception);
    } finally {
      setIsAddingAddress(false);
    }
  };

  return (
    <Stack direction="column" alignItems="center" sx={{ mb: '10px' }}>
      <ChooseAddress
        onAddressSelected={(address, apartment) => {
          selectNewAddress(address, apartment);
        }}
      />
      <Collapse sx={{ marginTop: '10px' }} in={!isUniqueAddress}>
        <Typography sx={{ color: 'error.main' }}>{t('address:addressExist')}</Typography>
      </Collapse>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: '10px', width: '100%' }}>
        <CancelButton onClick={() => onCancel?.()} variant="contained">
          {t('account:cancel')}
        </CancelButton>
        <Button
          sx={{
            borderRadius: '15px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          }}
          variant="contained"
          disabled={isAddingAddress || !newAddress || !isUniqueAddress}
          onClick={addNewAddress}
          loading={isAddingAddress}
          loadingIndicator={<ButtonLoadingIndicator />}
        >
          {t('address:add')}
        </Button>
      </Stack>
    </Stack>
  );
};
